import LegalSection, { LegalSection as LegalSectionType } from '@/components/ui/LegalSection';
import { createFileRoute } from '@tanstack/react-router';
import { Shield } from 'lucide-react';
import { useEffect } from 'react';

export const Route = createFileRoute('/_public/privacy-policy')({
    component: PrivacyPage,
})

export default function PrivacyPage() {
    useEffect(() => {
        document.title = 'Privacy Policy | Swim Homes';
    }, []);

    const sections: LegalSectionType[] = [
        {
            title: "",
            content: [
                "At Swim Homes (\"Swim\", \"we\", \"us\", or \"our\"), we are committed to protecting the privacy of our users. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our website and services at https://www.swimhomes.ca (the \"Platform\"), including our AI-powered features such as \"the Captain\". This Privacy Policy forms a part of our Terms and Conditions and is governed by the laws of the Province of Ontario.",
                "By using our services, you agree to be bound by this Privacy Policy as well as our Terms and Conditions. If you do not agree with the terms of this Privacy Policy, please refrain from using the Platform."
            ],
        },
        {
            title: "1. Information We Collect",
            content: [
                "We may collect personal and non-personal information from you when you use our Platform. This information is used to provide you with our services and improve your experience on the Platform."
            ],
            subsections: [
                {
                    title: "a. Personal Information",
                    content: [
                        "\"Personal Information\" refers to information that can be used to identify you directly or indirectly, including but not limited to:",
                        "Name",
                        "Email address",
                        "Phone number",
                        "Postal address",
                        "Payment information (if applicable)",
                        "Property details and preferences",
                        "We collect Personal Information when you:",
                        "Register an account on our Platform",
                        "List or search for a property on our Platform",
                        "Contact us with inquiries or support requests",
                        "Subscribe to our newsletter or updates",
                        "Interact with our AI tools, including \"the Captain\""
                    ]
                },
                {
                    title: "b. Non-Personal Information",
                    content: [
                        "We may also collect non-personal information that does not identify you, including:",
                        "Browser and device information (e.g., IP address, browser type, operating system)",
                        "Usage data (e.g., pages viewed, links clicked, time spent on the Platform)",
                        "Aggregated data regarding real estate transactions and market trends",
                        "AI interaction data (e.g., queries, responses, usage patterns)",
                        "This information helps us analyze how the Platform is used and improve our services."
                    ]
                }
            ]
        },
        {
            title: "2. AI-Specific Data Collection",
            content: [
                "When you interact with our AI tools, including \"the Captain\", we collect and process additional information:"
            ],
            subsections: [
                {
                    title: "a. Conversation Data",
                    content: [
                        "We record and store the content of your conversations with our AI tools, including your queries and the AI's responses. This data is used to improve the AI's performance, train our systems, ensure quality, and enhance the user experience."
                    ]
                },
                {
                    title: "b. Usage Patterns",
                    content: [
                        "We collect information about how you use our AI tools, including frequency of use, types of queries, and feature utilization. This helps us understand user needs and improve our AI offerings."
                    ]
                },
                {
                    title: "c. Feedback Data",
                    content: [
                        "Any feedback you provide about our AI tools is collected and used to enhance the functionality and accuracy of these features."
                    ]
                }
            ]
        },
        {
            title: "3. How We Use Your Information",
            content: [
                "We use the information we collect for various purposes, including:",
                "To provide and maintain the Platform and its services",
                "To facilitate real estate transactions between buyers and sellers",
                "To communicate with you regarding your account, listings, or inquiries",
                "To develop, train, and improve our AI tools and features",
                "To send you marketing communications, newsletters, or promotional materials (you can opt out at any time)",
                "To process payments for services provided by Swim Homes",
                "To improve and optimize the functionality of the Platform",
                "To comply with legal obligations and protect the rights and safety of Swim Homes, its users, and others"
            ]
        },
        {
            title: "4. Disclosure of Your Information",
            content: [
                "Swim Homes does not sell your personal information to third parties. However, we may disclose your information in the following circumstances:"
            ],
            subsections: [
                {
                    title: "a. With Third-Party Service Providers",
                    content: [
                        "We may share your information with trusted third-party service providers who assist us in operating the Platform, processing payments, managing listings, providing AI services, or performing other services on our behalf. These service providers are required to maintain the confidentiality of your information and are prohibited from using your information for any purpose other than the services provided to us."
                    ]
                },
                {
                    title: "b. With Third-Party Realtor Partners",
                    content: [
                        "Swim Homes may share property information and certain personal details with our third-party realtor partners for the purpose of posting your property listings on MLS or other real estate listing services. Only the information necessary to complete the MLS or listing posting is shared with our realtor partners. By using Swim Homes and listing properties, you consent to the sharing of your information for this purpose."
                    ]
                },
                {
                    title: "c. For Legal Purposes",
                    content: [
                        "We may disclose your information if required to do so by law, or in response to a valid legal request, such as a court order, subpoena, or government investigation."
                    ]
                },
                {
                    title: "d. In the Event of a Business Transaction",
                    content: [
                        "In the event that Swim Homes is involved in a merger, acquisition, sale of assets, or other business transaction, your personal information may be transferred as part of that transaction. We will notify you of any changes in ownership or the use of your personal information."
                    ]
                },
                {
                    title: "e. No Sale of Customer Data",
                    content: [
                        "Swim Homes does not sell customer data for any reason whatsoever. Your personal information will only be transferred to third-party service providers as required to operate the Platform, such as for payment processing, customer support, AI services, or property listing services."
                    ]
                }
            ]
        },
        {
            title: "5. AI Data Usage and Retention",
            content: [
                "Your interactions with our AI tools may be used to train and improve our AI systems. This process involves analyzing patterns in usage data to enhance the AI's ability to provide relevant and accurate information."
            ],
            subsections: [
                {
                    title: "a. Training and Improvement",
                    content: [
                        "Your interactions with our AI tools may be used to train and improve our AI systems. This process involves analyzing patterns in usage data to enhance the AI's ability to provide relevant and accurate information."
                    ]
                },
                {
                    title: "b. Data Retention",
                    content: [
                        "We retain AI interaction data for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. You may request deletion of your AI interaction data as described in the \"Your Rights and Choices\" section."
                    ]
                },
                {
                    title: "c. Data Anonymization",
                    content: [
                        "Where possible, we anonymize AI interaction data before using it for training purposes to protect your privacy."
                    ]
                }
            ]
        },
        {
            title: "6. Cookies and Tracking Technologies",
            content: [
                "Swim Homes uses cookies and other tracking technologies to collect non-personal information and enhance your experience on the Platform. Cookies are small text files stored on your device that allow us to remember your preferences and track usage data.",
                "We use cookies for purposes such as:",
                "Remembering your login information",
                "Keeping track of your preferences",
                "Analyzing user behavior on the Platform",
                "Providing personalized content and recommendations",
                "Supporting AI functionality and personalization",
                "You can control the use of cookies through your browser settings, but disabling cookies may limit your ability to use certain features of the Platform, including some AI features."
            ]
        },
        {
            title: "7. Security of Your Information",
            content: [
                "We take the security of your personal information seriously. Swim Homes implements reasonable security measures to protect your information from unauthorized access, use, or disclosure. These measures include encryption, secure storage systems, and access controls.",
                "However, no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee the absolute security of your information. You acknowledge and agree that any transmission of information is at your own risk."
            ]
        },
        {
            title: "8. AI-Specific Security Measures",
            content: [
                "We implement additional security measures specific to our AI tools, including:",
                "Encryption of AI conversation data",
                "Access controls limiting which employees can view AI interaction data",
                "Regular security audits of AI systems and data storage",
                "Automated monitoring for unusual or unauthorized access to AI data"
            ]
        },
        {
            title: "9. Third-Party Links and Services",
            content: [
                "The Platform may contain links to third-party websites, services, or applications that are not owned or controlled by Swim Homes. These third-party sites may collect personal information from you. We are not responsible for the privacy practices or the content of such third-party websites.",
                "We encourage you to read the privacy policies of any third-party websites or services you interact with while using our Platform."
            ]
        },
        {
            title: "10. Your Rights and Choices",
            content: [
                "As a user of Swim Homes, you have certain rights regarding your personal information. These rights include:"
            ],
            subsections: [
                {
                    title: "a. Access and Correction",
                    content: [
                        "You have the right to access the personal information we hold about you, including records of your AI interactions. If any information is inaccurate or incomplete, you may request that we correct or update it."
                    ]
                },
                {
                    title: "b. Data Deletion",
                    content: [
                        "You may request that we delete your personal information from our systems, including AI interaction data, subject to any legal obligations or legitimate interests we may have to retain such information."
                    ]
                },
                {
                    title: "c. Opt-Out of Marketing Communications",
                    content: [
                        "You have the right to opt out of receiving marketing or promotional emails from Swim Homes. You can do so by following the unsubscribe instructions provided in the email or by contacting us directly."
                    ]
                },
                {
                    title: "d. AI Interaction Controls",
                    content: [
                        "You can choose to limit your interactions with our AI tools or request that specific AI conversations not be stored or used for training purposes by contacting us."
                    ]
                }
            ]
        },
        {
            title: "11. Children's Privacy",
            content: [
                "Swim Homes does not knowingly collect or solicit personal information from individuals under the age of 18. If you are under 18, please do not use the Platform or provide any information to us. If we become aware that we have collected personal information from a person under the age of 18, we will take steps to delete that information as soon as possible."
            ]
        },
        {
            title: "12. Changes to the Privacy Policy",
            content: [
                "Swim Homes may update this Privacy Policy from time to time. When we do, we will post the updated Privacy Policy on this page with the new effective date. Your continued use of the Platform after any changes to this Privacy Policy constitutes your acceptance of the updated terms.",
                "We encourage you to review this Privacy Policy periodically for any updates or changes."
            ]
        },
        {
            title: "13. Dispute Resolution",
            content: [
                "In the event of a dispute regarding the collection or use of your personal information, you agree to first contact us to resolve the issue informally. If the dispute cannot be resolved through negotiation, it will be referred to arbitration in Ontario in accordance with the dispute resolution terms of our Terms and Conditions."
            ]
        },
        {
            title: "14. Governing Law",
            content: [
                "This Privacy Policy is governed by and construed in accordance with the laws of the Province of Ontario and the federal laws of Canada applicable therein."
            ]
        },
        {
            title: "15. Force Majeure",
            content: [
                "Swim Homes shall not be liable for any failure to perform its obligations under this Privacy Policy where such failure is as a result of Acts of God (including fire, flood, earthquake, storm, hurricane or other natural disaster), war, invasion, act of foreign enemies, hostilities (whether war is declared or not), civil war, rebellion, revolution, insurrection, military or usurped power or confiscation, terrorist activities, nationalization, government sanction, blockage, embargo, labor dispute, strike, lockout or interruption or failure of electricity or internet service, or any other event beyond the reasonable control of Swim Homes."
            ]
        },
        {
            title: "16. Severability",
            content: [
                "If any provision of this Privacy Policy is found to be unenforceable or invalid under any applicable law, such unenforceability or invalidity shall not render this Privacy Policy unenforceable or invalid as a whole, and such provisions shall be deleted without affecting the remaining provisions herein."
            ]
        },
        {
            title: "17. Entire Agreement",
            content: [
                "This Privacy Policy, together with the Terms of Service and AI Disclaimer, constitute the entire agreement between you and Swim Homes regarding the privacy of your information, and supersede all prior and contemporaneous written or oral agreements, communications, and other understandings relating to the subject matter of this Privacy Policy."
            ]
        },
        {
            title: "18. Non-Waiver",
            content: [
                "The failure of Swim Homes to exercise or enforce any right or provision of this Privacy Policy shall not constitute a waiver of such right or provision. No waiver by Swim Homes of any term or condition set forth in this Privacy Policy shall be deemed a further or continuing waiver of such term or condition or a waiver of any other term or condition."
            ]
        },
        {
            title: "19. Contact Information",
            content: [
                "If you have any questions or concerns about this Privacy Policy or the handling of your personal information, please contact us at: support@swimhomes.ca"
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <div className="grow py-12">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center gap-3 mb-8">
                        <Shield className="h-7 w-7 text-primary" />
                        <h1 className="text-3xl font-bold">Privacy Policy</h1>
                    </div>

                    <div className="mb-8">
                        <p className="text-muted-foreground mb-2">Last updated: March 03, 2025</p>

                        <div className="mt-6 prose prose-lg max-w-none">
                            <p>
                                {sections[0].content && sections[0].content[0]}
                            </p>
                            <p>
                                {sections[0].content && sections[0].content[1]}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-10 mt-12">
                        {sections.filter(section => section.title).map((section, index) => (
                            <div key={index} className="prose max-w-none">
                                <LegalSection section={section} />
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 border-t pt-6 mb-12">
                        <p className="text-muted-foreground">
                            If you have any questions about our Privacy Policy, please contact us at{' '}
                            <a href="mailto:support@swimhomes.ca" className="text-primary hover:underline">
                                support@swimhomes.ca
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}